// Team/member endpoints (admin JWT). Shapes mirror the API exactly:
//   - Member (OrgAdmin) → api/src/services/org-service.ts
//   - the routes        → api/src/http/routes/admin.ts (GET/POST 201/DELETE 204)
//
// role is a literal union (vault: Union_Type — owner|admin are "admin-capable",
// member is restricted). Only admin|member are invitable; 'owner' is the founder.
// Success bodies are Zod-validated at the boundary (vault Type_Assertion) so
// envelope drift fails loud as a ContractError, not a downstream undefined.
import { z } from "zod";
import { apiFetch } from "./client";

export type OrgRole = "owner" | "admin" | "member";
export type InvitableRole = "admin" | "member";

export interface Member {
  id: string;
  orgId: string;
  email: string;
  name?: string;
  role: OrgRole;
  createdAt: string;
  /** True for an invited member who hasn't accepted yet (COG-7a). Absent = active. */
  pending?: boolean;
}

const memberSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  email: z.string(),
  name: z.string().optional(),
  role: z.enum(["owner", "admin", "member"]),
  createdAt: z.string(),
  pending: z.boolean().optional(),
}) satisfies z.ZodType<Member>;

const membersResponseSchema = z.object({ members: z.array(memberSchema) });

export interface InviteMemberInput {
  email: string;
  name?: string;
  role?: InvitableRole;
}

export async function listMembers(signal?: AbortSignal): Promise<Member[]> {
  const res = await apiFetch("/admin/members", { auth: true, signal, schema: membersResponseSchema });
  return res.members;
}

export function inviteMember(body: InviteMemberInput): Promise<Member> {
  // Match the API contract exactly: `name` is optional-OR-min-1 (the route's Zod
  // rejects an empty string with 400), so OMIT a blank name rather than sending
  // "". Likewise omit an unset role (the API defaults it). Sending `name: ""`
  // would 400 the common "invite by email, no name" path.
  const payload: InviteMemberInput = { email: body.email.trim() };
  const name = body.name?.trim();
  if (name) payload.name = name;
  if (body.role) payload.role = body.role;
  return apiFetch("/admin/members", { method: "POST", auth: true, body: payload, schema: memberSchema });
}

export function removeMember(id: string): Promise<void> {
  return apiFetch(`/admin/members/${encodeURIComponent(id)}`, { method: "DELETE", auth: true });
}

/**
 * Change a member's role (promote member→admin / demote admin→member). The owner's
 * role is immutable server-side; the last admin-capable member can't be demoted —
 * both surface as an INVALID_INPUT the screen shows.
 */
export function changeMemberRole(id: string, role: InvitableRole): Promise<Member> {
  return apiFetch(`/admin/members/${encodeURIComponent(id)}`, {
    method: "PATCH",
    auth: true,
    body: { role },
    schema: memberSchema,
  });
}

/** owner|admin are admin-capable; member is restricted (mirrors api admin-authz). */
export function isAdminRole(role: OrgRole): boolean {
  return role === "owner" || role === "admin";
}
